// AssistantWorking.component.tsx
// The "working" line of the assistant panel while a turn runs: a drawn
// four-point sparkle in the brand colour that turns and pulses, a word that
// changes every few seconds, and the seconds elapsed once a turn takes a
// while. Drawn, not a text character: some sparkle characters render as
// coloured emoji on Windows. Still for reduced motion.

import { useEffect, useState } from "react"

/** The words shown in turn; the first is the one shown at once. */
export const WORKING_WORDS = ["Working…", "Thinking…", "Checking…", "Drafting…"]

const WORD_MS = 3000
/** Seconds after which the elapsed time is shown. */
const SHOW_SECONDS_AFTER = 5
/** The brand colour (antd colorPrimary). */
const BRAND = "#36C3ED"

const prefersReducedMotion = (): boolean =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches

/** A four-point sparkle with a small one beside it. */
const Sparkle = ({ still }: { still: boolean }) => (
  <span
    className={`inline-flex h-4 w-4 items-center justify-center ${
      still ? "" : "animate-spin [animation-duration:3.2s]"
    }`}
    aria-hidden="true"
    data-testid="assistant-sparkle"
  >
    <svg
      viewBox="0 0 24 24"
      className={`h-4 w-4 ${still ? "" : "animate-pulse [animation-duration:1.6s]"}`}
    >
      <path
        d="M12 1.5c.5 4.9 2.6 7.9 9.5 10.5-6.9 2.6-9 5.6-9.5 10.5-.5-4.9-2.6-7.9-9.5-10.5C9.4 9.4 11.5 6.4 12 1.5Z"
        fill={BRAND}
      />
      <circle cx="20" cy="4.5" r="1.6" fill={BRAND} opacity="0.7" />
    </svg>
  </span>
)

const AssistantWorking = () => {
  const [seconds, setSeconds] = useState(0)
  const [wordIndex, setWordIndex] = useState(0)
  const still = prefersReducedMotion()

  useEffect(() => {
    const id = window.setInterval(() => setSeconds((s) => s + 1), 1000)
    return () => window.clearInterval(id)
  }, [])

  useEffect(() => {
    if (still) return
    const id = window.setInterval(
      () => setWordIndex((i) => (i + 1) % WORKING_WORDS.length),
      WORD_MS
    )
    return () => window.clearInterval(id)
  }, [still])

  return (
    <div
      className="flex items-center gap-2 text-xs text-gray-500"
      role="status"
      aria-label="The assistant is working"
    >
      <Sparkle still={still} />
      <span>{WORKING_WORDS[wordIndex]}</span>
      {seconds >= SHOW_SECONDS_AFTER && <span className="text-gray-400">{seconds}s</span>}
    </div>
  )
}

export default AssistantWorking
